import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Car, LogOut, Camera, Check, ChevronRight, Loader2, MessageCircle, ArrowLeft, MapPin } from "lucide-react";
import { memberLogin } from "@/lib/api";
import { techServices, techAssign, techSetState, techNotes, techUploadPhoto, mediaUrl } from "@/lib/api";

const GOLD = "#C7A668";
const WHATSAPP_NUMBER = "525578988731";
const REQUIRED_PAIRS = 6;

const STATE_LABELS = {
  1: "Solicitud recibida", 2: "Confirmado", 3: "Pago recibido", 4: "Técnico asignado",
  5: "En camino", 6: "En servicio", 7: "Servicio completado", 8: "Reporte entregado",
};

function Login({ onDone }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setErr(""); setBusy(true);
    try { const r = await memberLogin(form.email.trim(), form.password); localStorage.setItem("tb_tech", r.token); onDone(); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <div className="grain min-h-screen bg-[#0A0A0B] text-[#F2EFE8] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="font-mono text-xs tracking-widest uppercase" style={{ color: GOLD }}>Dukk · Técnicos</div>
        <h1 className="font-display text-3xl text-white mt-3 mb-6" style={{ fontWeight: 300 }}>Acceso de técnico</h1>
        <div className="space-y-4">
          <input data-testid="tech-email" type="email" placeholder="Correo" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-sm px-4 py-3 text-white outline-none" style={{ background: "#121214", border: "1px solid rgba(255,255,255,.15)" }} />
          <input data-testid="tech-password" type="password" placeholder="Contraseña" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-sm px-4 py-3 text-white outline-none" style={{ background: "#121214", border: "1px solid rgba(255,255,255,.15)" }} />
          {err && <p data-testid="tech-error" className="text-sm" style={{ color: "#e08a8a" }}>{String(err)}</p>}
          <button data-testid="tech-login-btn" onClick={submit} disabled={busy} className="w-full rounded-sm py-3 font-semibold" style={{ background: GOLD, color: "#0a0a0a", border: "none", cursor: "pointer" }}>{busy ? "…" : "Entrar"}</button>
        </div>
      </div>
    </div>
  );
}

function PairSlot({ sid, pair, media, mtoken, atoken, onUploaded }) {
  const [busy, setBusy] = useState("");
  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    try { await techUploadPhoto(mtoken, atoken, sid, kind, pair, file); await onUploaded(); } catch (e) { alert(e?.response?.data?.detail || e.message); }
    setBusy("");
  };
  const cell = (kind, label) => {
    const m = media[kind];
    return (
      <label data-testid={`pair-${pair}-${kind}`} className="relative flex items-center justify-center rounded-sm overflow-hidden cursor-pointer" style={{ aspectRatio: "1/1", background: "#0d0d0f", border: `1px solid ${m ? GOLD : "rgba(255,255,255,.14)"}` }}>
        {m ? <img src={mediaUrl(m.path)} alt={label} className="w-full h-full object-cover" /> : (
          <div className="flex flex-col items-center gap-1 text-white/40">{busy === kind ? <Loader2 size={20} className="animate-spin" /> : <Camera size={20} />}<span className="font-mono text-[10px] tracking-widest uppercase">{label}</span></div>
        )}
        {m && <span className="absolute top-1 right-1 rounded-full flex items-center justify-center" style={{ width: 18, height: 18, background: GOLD }}><Check size={11} color="#0a0a0a" strokeWidth={3} /></span>}
        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(kind, e.target.files[0])} />
      </label>
    );
  };
  return (
    <div>
      <div className="font-mono text-[10px] tracking-widest uppercase text-white/35 mb-1.5">Zona {pair + 1}</div>
      <div className="grid grid-cols-2 gap-2">{cell("before", "Antes")}{cell("after", "Después")}</div>
    </div>
  );
}

function Detail({ svc, mtoken, atoken, reload, back }) {
  const [notes, setNotes] = useState(svc.techNotes || "");
  const [busy, setBusy] = useState(false);

  const media = svc.media || [];
  const pairsMap = {};
  media.forEach((m) => { pairsMap[m.pair] = pairsMap[m.pair] || {}; pairsMap[m.pair][m.kind] = m; });
  const completePairs = Object.values(pairsMap).filter((p) => p.before && p.after).length;
  const canComplete = completePairs >= REQUIRED_PAIRS;

  const assigned = svc.technicianId && svc.technicianId !== "" && svc.technicianId !== null;

  const doAssign = async () => { setBusy(true); try { await techAssign(mtoken, atoken, svc.serviceId, {}); await reload(); } catch (e) { alert(e?.response?.data?.detail || e.message); } setBusy(false); };
  const advance = async (to) => {
    setBusy(true);
    try { await techSetState(mtoken, atoken, svc.serviceId, to); await reload(); }
    catch (e) { alert(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const saveNotes = async () => { setBusy(true); try { await techNotes(mtoken, atoken, svc.serviceId, { techNotes: notes }); await reload(); } catch (_) {} setBusy(false); };

  const clientMsgs = {
    2: `Hola ${svc.clientName || ""}, tu servicio Dukk está confirmado. Te compartimos tu liga de seguimiento.`,
    4: `Hola ${svc.clientName || ""}, tu técnico ${svc.technicianName || "Dukk"} atenderá tu ${svc.carModel || "auto"}.`,
    5: `Vamos en camino a tu ${svc.carModel || "auto"}. Por favor comparte tu ubicación.`,
    6: `Llegamos. Empezamos con tu ${svc.carModel || "auto"}. Te avisamos al terminar.`,
    7: `Listo. Tu ${svc.carModel || "auto"} está impecable. Aquí está el reporte con fotos antes/después.`,
  };
  const notifyClient = () => {
    const trackUrl = `${window.location.origin}/servicio/${svc.token}`;
    const msg = encodeURIComponent(`${clientMsgs[svc.state] || "Actualización de tu servicio Dukk."}\n\nSeguimiento: ${trackUrl}`);
    const phone = (svc.clientContact || "").replace(/[^0-9]/g, "");
    const num = phone.length >= 10 ? (phone.length === 10 ? "52" + phone : phone) : WHATSAPP_NUMBER;
    window.open(`https://wa.me/${num}?text=${msg}`, "_blank");
  };

  const next = svc.state + 1;
  return (
    <div className="max-w-lg mx-auto pb-32">
      <button onClick={back} data-testid="tech-back" className="flex items-center gap-2 font-mono text-xs text-white/50 mb-4" style={{ background: "none", border: "none", cursor: "pointer" }}><ArrowLeft size={14} /> Volver</button>
      <div className="rounded-sm p-5" style={{ background: "#121214", border: "1px solid rgba(199,166,104,.22)" }}>
        <div className="flex items-center gap-2"><Car size={20} color={GOLD} /><span className="font-display text-xl text-white" style={{ fontWeight: 300 }}>{[svc.carMake, svc.carModel].filter(Boolean).join(" ") || "Auto"}</span></div>
        <div className="text-white/45 text-sm mt-1">{[svc.sizeLabel, svc.carColor, svc.packageName].filter(Boolean).join(" · ")}</div>
        <div className="font-mono text-xs text-white/35 mt-1">{svc.serviceId} · {svc.clientName || "Cliente"} {svc.scheduledWhen ? `· ${svc.scheduledWhen}` : ""}</div>
        <div className="mt-3 inline-flex items-center gap-2 font-mono text-xs px-3 py-1.5 rounded-full" style={{ background: "rgba(199,166,104,.12)", color: GOLD }}><span>Etapa {svc.state}/8</span> · {STATE_LABELS[svc.state]}</div>
      </div>

      {/* Actions */}
      <div className="mt-5 space-y-3">
        {!assigned && (
          <button onClick={doAssign} disabled={busy} data-testid="tech-take-btn" className="w-full rounded-sm py-3 font-semibold flex items-center justify-center gap-2" style={{ background: GOLD, color: "#0a0a0a", border: "none", cursor: "pointer" }}>Tomar servicio</button>
        )}

        <button onClick={notifyClient} data-testid="tech-notify-btn" className="w-full rounded-sm py-3 font-mono text-sm flex items-center justify-center gap-2" style={{ background: "transparent", color: "#25D366", border: "1px solid rgba(37,211,102,.4)", cursor: "pointer" }}><MessageCircle size={15} /> Notificar al cliente por WhatsApp</button>

        {svc.state < 8 && next < 7 && (
          <button onClick={() => advance(next)} disabled={busy} data-testid="tech-advance-btn" className="w-full rounded-sm py-3 font-semibold flex items-center justify-center gap-2" style={{ background: "#F2EFE8", color: "#0a0a0a", border: "none", cursor: "pointer" }}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <>Avanzar a: {STATE_LABELS[next]} <ChevronRight size={16} /></>}
          </button>
        )}
        {svc.state === 5 && <div className="flex items-start gap-2 text-white/45 text-xs"><MapPin size={13} className="mt-0.5 flex-none" color={GOLD} /> En esta etapa se le pide al cliente su ubicación desde su seguimiento.</div>}
      </div>

      {/* Photos required from state 6 onwards */}
      {svc.state >= 5 && svc.state < 8 && (
        <div className="mt-8" data-testid="tech-photos">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-mono text-sm tracking-widest uppercase text-white/50">Fotos antes / después</h3>
            <span className="font-mono text-xs" style={{ color: canComplete ? GOLD : "#e0a06a" }}>{completePairs}/{REQUIRED_PAIRS} pares</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Array.from({ length: Math.max(REQUIRED_PAIRS, Object.keys(pairsMap).length) }).map((_, i) => (
              <PairSlot key={i} sid={svc.serviceId} pair={i} media={pairsMap[i] || {}} mtoken={mtoken} atoken={atoken} onUploaded={reload} />
            ))}
          </div>

          <div className="mt-5">
            <div className="font-mono text-[10px] tracking-widest uppercase text-white/40 mb-1.5">Notas del técnico (visibles al cliente)</div>
            <textarea data-testid="tech-notes" value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes} rows={3} className="w-full rounded-sm px-3 py-2 text-white text-sm outline-none" style={{ background: "#101012", border: "1px solid rgba(255,255,255,.14)" }} placeholder="Ej. Piel tratada con acondicionador, rines detallados a mano…" />
          </div>

          {!canComplete && <p className="text-xs mt-3" style={{ color: "#e0a06a" }}>Debes subir al menos {REQUIRED_PAIRS} pares completos (antes + después) para poder completar el servicio.</p>}
          <button onClick={() => advance(7)} disabled={busy || !canComplete} data-testid="tech-complete-btn" className="w-full mt-4 rounded-sm py-3.5 font-semibold flex items-center justify-center gap-2" style={{ background: canComplete ? GOLD : "rgba(199,166,104,.25)", color: canComplete ? "#0a0a0a" : "rgba(255,255,255,.4)", border: "none", cursor: canComplete ? "pointer" : "not-allowed" }}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <><Check size={17} /> Marcar servicio como completado</>}
          </button>
        </div>
      )}

      {svc.state >= 7 && (
        <div className="mt-8">
          <button onClick={() => advance(8)} disabled={busy || svc.state >= 8} data-testid="tech-deliver-btn" className="w-full rounded-sm py-3 font-semibold flex items-center justify-center gap-2" style={{ background: svc.state >= 8 ? "rgba(255,255,255,.1)" : "#F2EFE8", color: svc.state >= 8 ? "rgba(255,255,255,.4)" : "#0a0a0a", border: "none", cursor: svc.state >= 8 ? "default" : "pointer" }}>
            {svc.state >= 8 ? <><Check size={16} /> Reporte entregado</> : "Marcar reporte como entregado"}
          </button>
          <a href={`/servicio/${svc.token}`} target="_blank" rel="noreferrer" className="block text-center mt-4 font-mono text-xs no-underline" style={{ color: GOLD }}>Ver reporte del cliente →</a>
        </div>
      )}
    </div>
  );
}

export default function TecnicoView() {
  const atoken = localStorage.getItem("tb_admin") || "";
  const [mtoken, setMtoken] = useState(localStorage.getItem("tb_tech") || "");
  const [services, setServices] = useState(null);
  const [selected, setSelected] = useState(null);
  const [denied, setDenied] = useState(false);

  const authed = !!atoken || !!mtoken;

  const load = useCallback(async () => {
    if (!authed) return;
    try {
      const d = await techServices(mtoken, atoken);
      setServices(d.services);
      setDenied(false);
      if (selected) { const upd = d.services.find((s) => s.serviceId === selected.serviceId); if (upd) setSelected(upd); }
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) { setDenied(true); localStorage.removeItem("tb_tech"); }
    }
  }, [mtoken, atoken, authed, selected]);

  useEffect(() => {
    if (authed) load();
    document.title = "Panel de técnico | Dukk";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  useEffect(() => {
    if (!authed || selected) return;
    const iv = setInterval(() => { if (document.visibilityState === "visible") load(); }, 25000);
    return () => clearInterval(iv);
  }, [authed, selected, load]);

  const logout = () => { localStorage.removeItem("tb_tech"); setMtoken(""); setServices(null); setSelected(null); };

  if (!authed || denied) return <Login onDone={() => { setDenied(false); setMtoken(localStorage.getItem("tb_tech") || ""); }} />;

  if (services === null) return <div className="grain min-h-screen bg-[#0A0A0B] flex items-center justify-center"><Loader2 className="animate-spin" color={GOLD} /></div>;

  return (
    <div className="grain min-h-screen bg-[#0A0A0B] text-[#F2EFE8] px-5 pt-16 pb-16">
      <Link to="/autos" className="no-underline font-display" style={{ position: "fixed", top: 16, left: 20, color: "#F2EFE8", fontSize: 18, letterSpacing: ".12em", fontWeight: 300, zIndex: 30 }}>DUKK</Link>
      {mtoken && <button onClick={logout} data-testid="tech-logout" className="font-mono text-xs flex items-center gap-1.5" style={{ position: "fixed", top: 16, right: 20, color: "#9aa0a7", background: "none", border: "1px solid rgba(255,255,255,.15)", borderRadius: 6, padding: "6px 12px", cursor: "pointer", zIndex: 30 }}><LogOut size={12} /> Salir</button>}

      {selected ? (
        <div className="pt-4"><Detail svc={selected} mtoken={mtoken} atoken={atoken} reload={load} back={() => setSelected(null)} /></div>
      ) : (
        <div className="max-w-lg mx-auto">
          <div className="font-mono text-xs tracking-widest uppercase mt-2" style={{ color: GOLD }}>Panel de técnico</div>
          <h1 className="font-display text-3xl text-white mt-2 mb-6" style={{ fontWeight: 300 }}>Servicios de hoy</h1>
          {services.length === 0 ? (
            <div className="rounded-sm border border-white/10 p-10 text-center text-white/40" style={{ background: "#101012" }}>No hay servicios activos.</div>
          ) : (
            <div className="space-y-3" data-testid="tech-service-list">
              {services.map((s) => (
                <motion.button key={s.serviceId} data-testid={`tech-service-${s.serviceId}`} onClick={() => setSelected(s)} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="w-full text-left rounded-sm p-4 flex items-center justify-between gap-3" style={{ background: "#121214", border: "1px solid rgba(255,255,255,.1)", cursor: "pointer" }}>
                  <div>
                    <div className="text-white font-medium flex items-center gap-2"><Car size={16} color={GOLD} /> {[s.carMake, s.carModel].filter(Boolean).join(" ") || "Auto"}</div>
                    <div className="font-mono text-xs text-white/40 mt-1">{s.serviceId} · {STATE_LABELS[s.state]}</div>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <span className="font-mono text-[10px] px-2 py-1 rounded-full" style={{ background: "rgba(199,166,104,.12)", color: GOLD }}>{s.state}/8</span>
                    <ChevronRight size={16} className="text-white/30" />
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
